import { clear, get, keys, set, update, values, del } from 'idb-keyval';
import { getAuthStateFromToken } from './auth';
import { AuthState } from '../containers/shared/auth/AuthContext';
import {
  ALL_INSTITUTION_TYPES_PREFIX,
  ALL_MINISTRIES_PREFIX,
  ECD_REG_TYPE_PREFIX,
  ECD_REQ_DOC_PREFIX,
  INSTITUTION_PREFIX,
  NON_CENTER_BASED_SURVEY_KEY,
  PARTIAL_CARE_FACILITY_SURVEY_KEY,
  USER_ID_PREFIX,
  PORTAL_ORGANISATION_PREFIX,
  ALL_COUNTRIES_PREFIX,
  ALL_PROVINCES_PREFIX,
  ALL_DISTRICT_MUNICIPALITIES_PREFIX,
  ALL_LOCAL_MUNICIPALITIES_PREFIX,
  ALL_ECD_REGISTRATION_TYPES,
  USER_PREFIX,
  ALL_DISTRICTS_PREFIX,
  // ALL_PEOPLE_PREFIX,
  PERSON_PREFIX,
  ALL_NATIONALITIES_PREFIX,
  ALL_GENDERS_PREFIX,
  ALL_POPULATION_GROUPS_PREFIX,
  ALL_HOME_LANGUAGES_PREFIX,
  ALL_DISABILITY_TYPES_PREFIX,
  ALL_PEOPLE_TYPES_PREFIX,
  ALL_GRADES_PREFIX,
  NOTIFICATION_PREFIX,
  ALL_INSTITUTION_PORTAL_USERS,
} from '../consts';
import {
  formatInstitutionData,
  formatECDRegistrationData,
  formatFormInstitutionToDBInstitution,
  formatFormRegistrationToDBRegistration,
  deepCopy,
} from '.';
import { GET_PORTAL_ORGANISATIONType } from '../graphql/queries/GetPortalOrganisation';
import { formatPeopleProfileFormType } from './format';

const isEqual = require('lodash.isequal');

// cookies
// const getCookie = (name: string) => document.cookie.split(';').some(c => c.trim().startsWith(name + '='));
// const deleteCookie = (name: string) => {
//   if (getCookie(name)) {
//     const resetCookie = `${name}=;Max-Age=-99999999;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT`;
//     document.cookie = resetCookie;
//     window.location.reload();
//   }
// };

// helpers
const userIDKey = (userID: string) => `${USER_ID_PREFIX}${userID}`;
const existingInstitutionKey = (id: string) => `${INSTITUTION_PREFIX}${id}`;
const newInstitutionKey = async () => {
  const baseKey = `${INSTITUTION_PREFIX}NEW`;
  const storedInstitutionKeys = (await keys()).filter(key => String(key).includes(baseKey));
  if (storedInstitutionKeys.length) {
    const numbers = storedInstitutionKeys.map(key => Number(String(key).replace(`${baseKey}_`, '')));
    return `${baseKey}_${Math.max(...numbers) + 1}`;
  }
  return `${baseKey}_${0}`;
};
const upsertInstitutionKey = (id: string) => `${INSTITUTION_PREFIX}${id}_UPDATED`;
const existingRegistrationKey = (id: string) => `${ECD_REG_TYPE_PREFIX}${id}`;
const newRegistrationKey = async () => {
  const baseKey = `${ECD_REG_TYPE_PREFIX}NEW`;
  const storedRegKeys = (await keys()).filter(key => String(key).includes(baseKey));
  if (storedRegKeys.length) {
    const numbers = storedRegKeys.map(key => Number(String(key).replace(`${baseKey}_`, '')));
    return `${baseKey}_${Math.max(...numbers) + 1}`;
  }
  return `${baseKey}_${0}`;
};

const upsertRegistrationKey = (id: string) => `${ECD_REG_TYPE_PREFIX}${id}_UPDATED`;
const ecdRequiredDocumentKey = (id: string) => `${ECD_REQ_DOC_PREFIX}${id}`;

const existingPersonKey = (id: string) => `${PERSON_PREFIX}${id}`;
const newPersonKey = async (userID?: string) => {
  const baseKey = `${PERSON_PREFIX}NEW`;
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  if (userValues) {
    // TODO: account for pagination
    const allKeys = Object.keys(userValues);
    const storedRegKeys = allKeys.filter(key => String(key).startsWith(baseKey));

    if (storedRegKeys.length) {
      const numbers = storedRegKeys.map(key => Number(String(key).replace(`${baseKey}_`, '')));
      return `${baseKey}_${Math.max(...numbers) + 1}`;
    }
  }

  return `${baseKey}_${0}`;
};
const upsertPersonKey = (id: string) => `${PERSON_PREFIX}${id}_UPDATED`;

const existingNotificationKey = (id: string) => `${NOTIFICATION_PREFIX}${id}`;
// const upsertNotificationKey = (id: string) => `${NOTIFICATION_PREFIX}${id}_UPDATED`;

const isValidInstitutionListKey = (key: any) =>
  String(key).includes(INSTITUTION_PREFIX) && !String(key).includes('_NEW');
const isValidRegistrationListKey = (key: any) => String(key).includes(ECD_REG_TYPE_PREFIX);
const isValidPeopleListKey = (key: any) => String(key).includes(PERSON_PREFIX);

const isChangedInstitutionListKey = (key: any) =>
  String(key).includes(INSTITUTION_PREFIX) && (String(key).includes('_UPDATED') || String(key).includes('_NEW'));
const isChangedRegistrationListKey = (key: any) =>
  String(key).includes(ECD_REG_TYPE_PREFIX) && (String(key).includes('_UPDATED') || String(key).includes('_NEW'));

const isChangedPeopleListKey = (key: any) =>
  String(key).includes(PERSON_PREFIX) && (String(key).includes('_UPDATED') || String(key).includes('_NEW'));

const isValidNotificationKey = (key: any) => String(key).includes(NOTIFICATION_PREFIX);

// general
export const updateIndexedDB = async (key: string, updater: (oldValue: any) => any) => await update(key, updater);
export const writeToIndexedDB = async (key: string, value: any) => await set(key, value);
//export const readFromIndexedDB = async (key: string) => await get(key);
export const readFromIndexedDB = async <T = any>(key: string) => await get<T>(key);
export const deleteKeyFromIndexedDB = async (key: string) => await del(key);
export const clearIndexedDB = async (userID: string) => {
  // get stored items under userid
  const allKeys = await keys();
  const storedUserKeys = allKeys.filter(key => String(key).includes(USER_ID_PREFIX));
  const storedUserKey = storedUserKeys[0];
  const currentUserKey = userIDKey(userID);

  if (Boolean(String(storedUserKey)) && String(storedUserKey) !== currentUserKey) {
    const userValues = await readFromIndexedDB(String(storedUserKey));
    const user = userValues && userValues[USER_PREFIX];
    const userName = user ? user.firstName + ' ' + user.lastName : storedUserKey;
    if (
      storedUserKey &&
      window.confirm(`User ${userName}'s information will be erased when you log in. Do you want to proceed?`)
    ) {
      console.info('Clearing indexed DB...');
      return await clear();
    }
    // TODO: figure out what should happen should they choose to cancel confirmation
    // console.info('Logging out...');
    // return deleteCookie(JWT_COOKIE_KEY);
  }
  return;
};

export const getUserInfoIfAny = async (): Promise<any> => {
  const allKeys = await keys();
  const storedUserKeys = allKeys.filter(key => String(key).includes(USER_ID_PREFIX));
  if (storedUserKeys.length === 0) return undefined;
  const storedUserKey = storedUserKeys[0] as string;
  const value = await readFromIndexedDB(storedUserKey);
  if (!value) return undefined;
  return value;
};

// institutions
export const getInstitutionById = async (id: string, userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  const existingValue = userValues && userValues[existingInstitutionKey(id)];
  const updatedValue = userValues && userValues[upsertInstitutionKey(id)];
  const newValue = userValues && userValues[id];

  // cater for when case is new institution
  return existingValue || updatedValue || newValue;
};

export const writeInstitutionsList = async (list: any[], userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  // GET current user object before appending
  //const currentUserObjInst = resolvedID && (await readFromIndexedDB(resolvedID));
  //const userValuesInst = currentUserObjInst ? deepCopy(currentUserObjInst) : {};
  //resolvedID && (await deleteKeyFromIndexedDB(resolvedID));
  //list.map(async item => (userValuesInst[existingInstitutionKey(item.node.id)] = item));
  //return resolvedID && (await writeToIndexedDB(resolvedID, userValuesInst));
  return (
    resolvedID &&
    (await updateIndexedDB(resolvedID, (oldValue: any) => {
      const value = oldValue ? deepCopy(oldValue) : {};
      list.map(async item => (value[existingInstitutionKey(item.node.id)] = item));
      return value;
    }))
  );
};

export const getInstitutionsList = async (userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  if (userValues) {
    // TODO: account for pagination
    const allKeys = Object.keys(userValues);
    const storedInstitutionKeys = allKeys.filter(isValidInstitutionListKey);

    return storedInstitutionKeys.map(key => userValues[key]);
  } else {
    return [];
  }
};

export const deleteChangedInstitutionsList = async (userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  const allKeys = Object.keys(userValues);
  const storedInstitutionKeys = allKeys.filter(isChangedInstitutionListKey);

  await Promise.all(storedInstitutionKeys.map(async key => delete userValues[String(key)]));

  return resolvedID && (await writeToIndexedDB(resolvedID, userValues));
};

export const getChangedInstitutionsList = async (userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  const allKeys = userValues && Object.keys(userValues);
  const storedInstitutionKeys = allKeys && Array.isArray(allKeys) && allKeys.filter(isChangedInstitutionListKey);

  return storedInstitutionKeys && storedInstitutionKeys.map((key: string) => userValues[key]);
};

export const upsertInstitutionLocally = async (institution: any, userID?: string) => {
  console.log('UpsertInstitutionLocally');
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = (resolvedID && (await readFromIndexedDB(resolvedID))) || {};

  if (institution.id) {
    // get existing and update
    const existingValue = userValues && userValues[existingInstitutionKey(institution.id)];
    const updatedValue = userValues && userValues[upsertInstitutionKey(institution.id)];
    const existing = existingValue || updatedValue;
    const isUnchanged: boolean = Boolean(existing) && isEqual(formatInstitutionData(existing.node), institution);

    // if changed, then check for existing upsert object and merge otherwise write new object
    if (!isUnchanged) {
      const existingUpdateObj = userValues && userValues[upsertInstitutionKey(institution.id)];

      if (existingUpdateObj) {
        resolvedID && (await deleteKeyFromIndexedDB(resolvedID));
        const merged = formatFormInstitutionToDBInstitution(Object.assign(existingUpdateObj, institution));
        userValues[upsertInstitutionKey(institution.id)] = { node: merged };
        delete userValues[existingInstitutionKey(institution.id)];

        resolvedID && (await writeToIndexedDB(resolvedID, userValues));

        return { institution: { id: institution.id } };
      }

      resolvedID && (await deleteKeyFromIndexedDB(resolvedID));
      const dbFormat = formatFormInstitutionToDBInstitution(institution);
      userValues[upsertInstitutionKey(institution.id)] = { node: dbFormat };
      delete userValues[existingInstitutionKey(institution.id)];

      resolvedID && (await writeToIndexedDB(resolvedID, userValues));

      return { institution: { id: institution.id } };
    } else {
      return { institution: { id: institution.id } };
    }
  }

  // create new value
  resolvedID && (await deleteKeyFromIndexedDB(resolvedID));
  const newInstKey = await newInstitutionKey();
  userValues[newInstKey] = { node: institution };

  resolvedID && (await writeToIndexedDB(resolvedID, userValues));
  return { institution: { id: newInstKey } };
};

// People

export const getPersonById = async (id: string, userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  const existingValue = userValues[existingPersonKey(id)];
  const updatedValue = userValues[upsertPersonKey(id)];
  const newValue = userValues[existingPersonKey('create_UPDATED')] || userValues[existingPersonKey('create')];
  // cater for when case is new person
  return existingValue || updatedValue || newValue;
};

export const writePeopleList = async (list: any[], userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  // GET current user object before appending
  // const currentUserObjECD = resolvedID && (await readFromIndexedDB(resolvedID));
  // const userValues = currentUserObjECD ? deepCopy(currentUserObjECD) : {};
  // resolvedID && (await deleteKeyFromIndexedDB(resolvedID));
  // list.map(async item => (userValues[existingRegistrationKey(item.node.id)] = item));
  // return resolvedID && (await writeToIndexedDB(resolvedID, userValues));
  return (
    resolvedID &&
    (await updateIndexedDB(resolvedID, (oldValue: any) => {
      const value = oldValue ? deepCopy(oldValue) : {};
      list.map(async item => (value[existingPersonKey(item.node.id)] = item));
      return value;
    }))
  );
};

export const getPeopleList = async (userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  // TODO: account for pagination
  if (userValues) {
    const allKeys = Object.keys(userValues);
    const storedPeopleKeys = allKeys.filter(isValidPeopleListKey);

    return storedPeopleKeys.map(key => userValues[key]);
  } else {
    return [];
  }
};

export const deleteChangedPeopleList = async (userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  const allKeys = Object.keys(userValues);
  const storedPeopleKeys = allKeys.filter(isChangedPeopleListKey);

  await Promise.all(storedPeopleKeys.map(async key => delete userValues[String(key)]));

  return resolvedID && (await writeToIndexedDB(resolvedID, userValues));
};

export const getChangedPeopleList = async (userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  const allKeys = userValues && Object.keys(userValues);
  const storedPeopleKeys = allKeys && Array.isArray(allKeys) && allKeys.filter(isChangedPeopleListKey);

  return storedPeopleKeys && storedPeopleKeys.map((key: string) => userValues[key]);
};

export const upsertPersonLocally = async (person: any, userID?: string, institutionId?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));

  if (person.id && !person.id.startsWith('create')) {
    // get existing and update
    const existingValue = userValues && userValues[existingPersonKey(person.id)];
    const updatedValue = userValues && userValues[upsertPersonKey(person.id)];
    const existing = existingValue || updatedValue;
    const copy = deepCopy(person);
    const isUnchanged: boolean = Boolean(existing) && isEqual(formatPeopleProfileFormType(existing.node), copy);

    // if changed, then check for existing upsert object and merge otherwise write new object
    if (!isUnchanged) {
      const existingUpdateObj = userValues && userValues[upsertPersonKey(person.id)];

      if (existingUpdateObj) {
        resolvedID && (await deleteKeyFromIndexedDB(resolvedID));
        const merged = formatPeopleProfileFormType(copy);
        userValues[upsertPersonKey(person.id)] = { node: merged };
        delete userValues[existingPersonKey(person.id)];

        resolvedID && (await writeToIndexedDB(resolvedID, userValues));
        return { person: { id: person.id } };
      }

      resolvedID && (await deleteKeyFromIndexedDB(resolvedID));
      const dbFormat = formatPeopleProfileFormType(copy);
      userValues[upsertPersonKey(person.id)] = { node: dbFormat };
      delete userValues[existingPersonKey(person.id)];
      resolvedID && (await writeToIndexedDB(resolvedID, userValues));
      return { person: { id: person.id } };
    } else {
      return { person: { id: person.id } };
    }
  } else {
    // create new value
    const newPsnKey = await newPersonKey(userID);
    const dbFormat = formatPeopleProfileFormType(person);
    userValues[newPsnKey] = { node: { ...dbFormat, institutionId } };

    resolvedID && (await writeToIndexedDB(resolvedID, userValues));
    return { person: { id: newPsnKey } };
  }
};

export const writeNationalities = async (nationalities: any[]) => {
  return await writeToIndexedDB(ALL_NATIONALITIES_PREFIX, nationalities);
};

export const writeGenders = async (genders: any[]) => {
  return await writeToIndexedDB(ALL_GENDERS_PREFIX, genders);
};

export const writePopulationGroups = async (populationGroups: any[]) => {
  return await writeToIndexedDB(ALL_POPULATION_GROUPS_PREFIX, populationGroups);
};

export const writeHomeLanguages = async (homeLanguages: any[]) => {
  return await writeToIndexedDB(ALL_HOME_LANGUAGES_PREFIX, homeLanguages);
};

export const writeDisabilityTypes = async (disabilityTypes: any[]) => {
  return await writeToIndexedDB(ALL_DISABILITY_TYPES_PREFIX, disabilityTypes);
};

export const writePeopleTypes = async (peopleTypes: any[]) => {
  return await writeToIndexedDB(ALL_PEOPLE_TYPES_PREFIX, peopleTypes);
};

export const writeGrades = async (grades: any[]) => {
  return await writeToIndexedDB(ALL_GRADES_PREFIX, grades);
};

export const getAllNationalities = async () => {
  return await readFromIndexedDB(ALL_NATIONALITIES_PREFIX);
};

export const getAllGenders = async () => {
  return await readFromIndexedDB(ALL_GENDERS_PREFIX);
};

export const getAllPopulationGroups = async () => {
  return await readFromIndexedDB(ALL_POPULATION_GROUPS_PREFIX);
};

export const getAllHomeLanguages = async () => {
  return await readFromIndexedDB(ALL_HOME_LANGUAGES_PREFIX);
};

export const getAllDisabilityTypes = async () => {
  return await readFromIndexedDB(ALL_DISABILITY_TYPES_PREFIX);
};

export const getAllPeopleTypes = async () => {
  return await readFromIndexedDB(ALL_PEOPLE_TYPES_PREFIX);
};

export const getAllGrades = async () => {
  return await readFromIndexedDB(ALL_GRADES_PREFIX);
};

// ECD Registrations
export const getECDRegistrationById = async (id: string, userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  const existingValue = userValues[existingRegistrationKey(id)];
  const updatedValue = userValues[upsertRegistrationKey(id)];
  const newValue =
    userValues[existingRegistrationKey('create_UPDATED')] || userValues[existingRegistrationKey('create')];
  // cater for when case is new registration
  return existingValue || updatedValue || newValue;
};

export const writeECDRegistrationsList = async (list: any[], userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  // GET current user object before appending
  // const currentUserObjECD = resolvedID && (await readFromIndexedDB(resolvedID));
  // const userValues = currentUserObjECD ? deepCopy(currentUserObjECD) : {};
  // resolvedID && (await deleteKeyFromIndexedDB(resolvedID));
  // list.map(async item => (userValues[existingRegistrationKey(item.node.id)] = item));
  // return resolvedID && (await writeToIndexedDB(resolvedID, userValues));
  return (
    resolvedID &&
    (await updateIndexedDB(resolvedID, (oldValue: any) => {
      const value = oldValue ? deepCopy(oldValue) : {};
      list.map(async item => (value[existingRegistrationKey(item.node.id)] = item));
      return value;
    }))
  );
};

export const getECDRegistrationsListPerInstitution = async (institutionId: string, userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = (resolvedID && (await readFromIndexedDB(resolvedID))) || {};

  // TODO: account for pagination
  const allKeys = Object.keys(userValues);
  const storedRegistrationsKeys = allKeys.filter(isValidRegistrationListKey);
  let registrations: any[] = [];
  storedRegistrationsKeys.forEach((key: string) => {
    if (
      userValues[key] &&
      userValues[key].node &&
      userValues[key].node.associatedEcdInstitution &&
      userValues[key].node.associatedEcdInstitution.id === institutionId
    ) {
      registrations.push(userValues[key]);
    }
  });
  return registrations;
};

export const getChangedRegistrationsList = async (userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  const allKeys = userValues && Object.keys(userValues);
  const storedRegistrationKeys = allKeys && Array.isArray(allKeys) && allKeys.filter(isChangedRegistrationListKey);

  return storedRegistrationKeys && storedRegistrationKeys.map((key: string) => userValues[key]);
};

export const deleteChangedRegistrationsList = async (userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  const allKeys = Object.keys(userValues);
  const storedRegistrationKeys = allKeys.filter(isChangedRegistrationListKey);

  await Promise.all(storedRegistrationKeys.map(async key => delete userValues[String(key)]));

  return resolvedID && (await writeToIndexedDB(resolvedID, userValues));
};

export const upsertRegistrationLocally = async (registration: any, step: string, userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));

  if (registration.id) {
    // get existing and update
    const existingValue = userValues && userValues[existingRegistrationKey(registration.id)];
    const updatedValue = userValues && userValues[upsertRegistrationKey(registration.id)];
    const existing = existingValue || updatedValue;
    const copy = deepCopy(registration);
    const isUnchanged: boolean =
      Boolean(existing) &&
      isEqual(formatECDRegistrationData(existing.node, existing.node.associatedEcdInstitution)[step], copy);

    // if changed, then check for existing upsert object and merge otherwise write new object
    if (!isUnchanged) {
      const existingUpdateObj = userValues && userValues[upsertRegistrationKey(registration.id)];

      if (existingUpdateObj) {
        resolvedID && (await deleteKeyFromIndexedDB(resolvedID));
        const merged = formatFormRegistrationToDBRegistration(copy);
        userValues[upsertRegistrationKey(registration.id)] = { node: merged };
        delete userValues[existingRegistrationKey(registration.id)];

        resolvedID && (await writeToIndexedDB(resolvedID, userValues));
        return { ecdRegistration: { id: registration.id } };
      }

      resolvedID && (await deleteKeyFromIndexedDB(resolvedID));
      const dbFormat = formatFormRegistrationToDBRegistration(copy);
      userValues[upsertRegistrationKey(registration.id)] = { node: dbFormat };
      delete userValues[existingRegistrationKey(registration.id)];
      resolvedID && (await writeToIndexedDB(resolvedID, userValues));
      return { ecdRegistration: { id: registration.id } };
    } else {
      return { ecdRegistration: { id: registration.id } };
    }
  } else {
    // create new value
    const newRegKey = await newRegistrationKey();
    const dbFormat = formatFormRegistrationToDBRegistration(registration);
    userValues[newRegKey] = { node: dbFormat };

    resolvedID && (await writeToIndexedDB(resolvedID, userValues));
    return { ecdRegistration: { id: newRegKey } };
  }
};

// Notifications

export const writeNotificationsList = async (list: any[], userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  // GET current user object before appending
  // const currentUserObjECD = resolvedID && (await readFromIndexedDB(resolvedID));
  // const userValues = currentUserObjECD ? deepCopy(currentUserObjECD) : {};
  // resolvedID && (await deleteKeyFromIndexedDB(resolvedID));
  // list.map(async item => (userValues[existingRegistrationKey(item.node.id)] = item));
  // return resolvedID && (await writeToIndexedDB(resolvedID, userValues));
  return (
    resolvedID &&
    (await updateIndexedDB(resolvedID, (oldValue: any) => {
      const value = oldValue ? deepCopy(oldValue) : {};
      list.map(async item => (value[existingNotificationKey(item.node.id)] = item));
      return value;
    }))
  );
};

export const getNotificationsList = async (userID?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(userID || computedUserID || '');
  const userValues = resolvedID && (await readFromIndexedDB(resolvedID));
  if (userValues) {
    // TODO: account for pagination
    const allKeys = Object.keys(userValues);
    const storedNotificationsKeys = allKeys.filter(isValidNotificationKey);

    return storedNotificationsKeys.map(key => userValues[key]);
  } else {
    return [];
  }
};
/**
 * ------------------------------------------------------------------------
 * The following are general queries for functioning of the forms offline.
 * Not necessary to save them by userID
 * ------------------------------------------------------------------------
 * */

export const writeECDRequiredDocumentsList = async (list: any[]) => {
  return list.map(async item => await writeToIndexedDB(ecdRequiredDocumentKey(item.id), item));
};

export const getECDReqDocsByRegType = async (regTypeId: string) => {
  const allStoredItems = await values();

  return allStoredItems.filter(
    (item: any) => item.registrationType && item.registrationType.some((type: any) => type.id === regTypeId)
  );
};

export const writeNCBSurvey = async (survey: any) => {
  return await writeToIndexedDB(NON_CENTER_BASED_SURVEY_KEY, survey);
};

export const writePCFSurvey = async (survey: any) => {
  return await writeToIndexedDB(PARTIAL_CARE_FACILITY_SURVEY_KEY, survey);
};

export const writeInstitutionTypes = async (types: any[]) => {
  return await writeToIndexedDB(ALL_INSTITUTION_TYPES_PREFIX, types);
};

export const writeMinistries = async (ministries: any[]) => {
  return await writeToIndexedDB(ALL_MINISTRIES_PREFIX, ministries);
};

export const getNCBSurvey = async () => {
  return await readFromIndexedDB(NON_CENTER_BASED_SURVEY_KEY);
};

export const getPCFSurvey = async () => {
  return await readFromIndexedDB(PARTIAL_CARE_FACILITY_SURVEY_KEY);
};

export const getAllInstitutionTypes = async () => {
  return await readFromIndexedDB(ALL_INSTITUTION_TYPES_PREFIX);
};

export const getAllMinistries = async () => {
  return await readFromIndexedDB(ALL_MINISTRIES_PREFIX);
};

export const writePortalOrganisation = async (organisation: GET_PORTAL_ORGANISATIONType, log?: string) => {
  const decoded: AuthState = getAuthStateFromToken();
  const userID = decoded && decoded.user.id;
  // console.log(`writePortalOrganisation ${!!log ? log : ''} : `, organisation);
  return await writeToIndexedDB(`${PORTAL_ORGANISATION_PREFIX}${userID}`, organisation);
};

export const getPortalOrganisation = async (): Promise<GET_PORTAL_ORGANISATIONType> => {
  const decoded: AuthState = getAuthStateFromToken();
  const userID = decoded && decoded.user.id;
  return (await readFromIndexedDB(`${PORTAL_ORGANISATION_PREFIX}${userID}`)) as GET_PORTAL_ORGANISATIONType;
};

export const writeCountries = async (countries: any[]) => {
  return await writeToIndexedDB(ALL_COUNTRIES_PREFIX, countries);
};

export const writeProvinces = async (provinces: any[]) => {
  return await writeToIndexedDB(ALL_PROVINCES_PREFIX, provinces);
};

export const writeECDRegistrationTypes = async (registrationTypes: any[]) => {
  return await writeToIndexedDB(ALL_ECD_REGISTRATION_TYPES, registrationTypes);
};

export const writeDistrictMunicipalities = async (districtMunicipalities: any[]) => {
  return await writeToIndexedDB(ALL_DISTRICT_MUNICIPALITIES_PREFIX, districtMunicipalities);
};

export const writeLocalMunicipalities = async (localMunicipalities: any[]) => {
  return await writeToIndexedDB(ALL_LOCAL_MUNICIPALITIES_PREFIX, localMunicipalities);
};

export const writeDistricts = async (districts: any[]) => {
  return await writeToIndexedDB(ALL_DISTRICTS_PREFIX, districts);
};

export const writeInstitutionPortalUsers = async (users: any[]) => {
  return await writeToIndexedDB(ALL_INSTITUTION_PORTAL_USERS, users);
};

export const writeUserInformation = async (user: any) => {
  const decoded: AuthState = getAuthStateFromToken();
  const computedUserID = decoded.user.id;
  const resolvedID = userIDKey(user.id || computedUserID || '');
  if (resolvedID) {
    // const currentUserData = await readFromIndexedDB(resolvedID);
    // const writeUserValues = currentUserData ? deepCopy(currentUserData) : {};
    // await deleteKeyFromIndexedDB(resolvedID);
    // writeUserValues[USER_PREFIX] = user;
    // await writeToIndexedDB(resolvedID, writeUserValues);
    await updateIndexedDB(resolvedID, (oldValue: any) => {
      const value = oldValue ? deepCopy(oldValue) : {};
      value[USER_PREFIX] = user;
      return value;
    });
  }
};

export const getAllCountries = async () => {
  return await readFromIndexedDB(ALL_COUNTRIES_PREFIX);
};

export const getAllProvinces = async () => {
  return await readFromIndexedDB(ALL_PROVINCES_PREFIX);
};

export const getECDRegistrationTypes = async () => {
  return await readFromIndexedDB(ALL_ECD_REGISTRATION_TYPES);
};

export const getAllDistrictMunicipalities = async () => {
  return await readFromIndexedDB(ALL_DISTRICT_MUNICIPALITIES_PREFIX);
};

export const getAllLocalMunicipalities = async () => {
  return await readFromIndexedDB(ALL_LOCAL_MUNICIPALITIES_PREFIX);
};

export const getAllDistricts = async () => {
  return await readFromIndexedDB(ALL_DISTRICTS_PREFIX);
};

export const getAllInstitutionPortalUsers = async () => {
  return await readFromIndexedDB(ALL_DISTRICTS_PREFIX);
};

export type AuthenticateUserOrganisationsType = {
  id: string;
  isDefault: boolean;
  role: {
    name: string;
  };
  registrationData:
    | {
        name: string;
      }
    | null
    | undefined;
  organisation: {
    id: string;
    isFunder: boolean;
    isInstitution: boolean;
    isServiceProvider: boolean;
    funder?: {
      id: string;
      name: string;
    } | null;
    serviceProvider?: {
      id: string;
      name: string;
    } | null;
    institution?: {
      id: string;
      name: string;
    } | null;
  };
}[];

export type AuthenticateUserType =
  | {
      token: string;
      user: {
        id: string;
        organisations: AuthenticateUserOrganisationsType;
      };
    }
  | null
  | undefined;

export const writeUserData = async (value: AuthenticateUserType, userId?: string) => {
  if (!userId) userId = getAuthStateFromToken().user.id;
  return await writeToIndexedDB(`${USER_PREFIX}DATA_${userId}`, value);
};

export const getUserData = async (userId?: string): Promise<AuthenticateUserType> => {
  if (!userId) userId = getAuthStateFromToken().user.id;
  return await readFromIndexedDB(`${USER_PREFIX}DATA_${userId}`);
};

export const getUserDataOrganisations = async (userId?: string): Promise<AuthenticateUserOrganisationsType> => {
  const userData = await getUserData(userId);
  if (!userData) return [];
  return userData.user.organisations;
};

export const getPeople = async (institutionId?: string) => {
  const allPeople = await getPeopleList();

  if (institutionId) {
    const result =
      allPeople &&
      allPeople.filter(
        ({ node }: any) =>
          (node.associatedInstitutions &&
            node.associatedInstitutions.find((b: any) => b.institution && b.institution.id === institutionId)) ||
          (node.institutions && node.institutions.find((b: any) => b.id === institutionId))
      );

    return result;
  } else {
    return allPeople;
  }
};
